import type { CollaborationRole } from '@/features/channel-router'

export interface CollaborationRoleTone {
  label: string
  text: string
  soft: string
  border: string
  bar: string
  badge: string
  chip: string
  focus: string
}

export const COLLABORATION_ROLE_TONES: Record<CollaborationRole, CollaborationRoleTone> = {
  user: {
    label: 'User',
    text: 'text-slate-950 dark:text-slate-50',
    soft: 'bg-slate-950/5 dark:bg-slate-50/10',
    border: 'border-slate-950/15 dark:border-slate-50/20',
    bar: 'bg-slate-950 dark:bg-slate-50',
    badge: 'border-border/70 bg-muted/50 text-slate-700 dark:text-slate-200',
    chip: 'border-border/70 bg-muted/45 text-slate-700 dark:text-slate-200',
    focus: 'focus-within:border-slate-400/60 focus-within:ring-slate-400/20',
  },
  cogo: {
    label: 'Coogo',
    text: 'text-foreground',
    soft: 'bg-muted/55',
    border: 'border-border/70',
    bar: 'bg-cyan-400',
    badge: 'border-border/70 bg-muted/50 text-muted-foreground',
    chip: 'border-border/70 bg-muted/45 text-muted-foreground',
    focus: 'focus-within:border-border focus-within:ring-border/30',
  },
  qwen: {
    label: '검토',
    text: 'text-muted-foreground',
    soft: 'bg-muted/55',
    border: 'border-border/70',
    bar: 'bg-violet-400',
    badge: 'border-border/70 bg-muted/50 text-muted-foreground',
    chip: 'border-border/70 bg-muted/45 text-muted-foreground',
    focus: 'focus-within:border-border focus-within:ring-border/30',
  },
  webmcp: {
    label: '화면',
    text: 'text-muted-foreground',
    soft: 'bg-muted/55',
    border: 'border-border/70',
    bar: 'bg-sky-400',
    badge: 'border-border/70 bg-muted/50 text-muted-foreground',
    chip: 'border-border/70 bg-muted/45 text-muted-foreground',
    focus: 'focus-within:border-border focus-within:ring-border/30',
  },
  source: {
    label: '자료',
    text: 'text-muted-foreground',
    soft: 'bg-muted/55',
    border: 'border-border/70',
    bar: 'bg-amber-400',
    badge: 'border-border/70 bg-muted/50 text-muted-foreground',
    chip: 'border-border/70 bg-muted/45 text-muted-foreground',
    focus: 'focus-within:border-border focus-within:ring-border/30',
  },
  app: {
    label: '앱',
    text: 'text-muted-foreground',
    soft: 'bg-muted/55',
    border: 'border-border/70',
    bar: 'bg-blue-500',
    badge: 'border-border/70 bg-muted/50 text-muted-foreground',
    chip: 'border-border/70 bg-muted/45 text-muted-foreground',
    focus: 'focus-within:border-border focus-within:ring-border/30',
  },
  runtime: {
    label: '동작',
    text: 'text-muted-foreground',
    soft: 'bg-muted/55',
    border: 'border-border/70',
    bar: 'bg-indigo-400',
    badge: 'border-border/70 bg-muted/50 text-muted-foreground',
    chip: 'border-border/70 bg-muted/45 text-muted-foreground',
    focus: 'focus-within:border-border focus-within:ring-border/30',
  },
  system: {
    label: '상태',
    text: 'text-muted-foreground',
    soft: 'bg-muted/60',
    border: 'border-border/70',
    bar: 'bg-muted-foreground/50',
    badge: 'border-border/70 bg-muted/60 text-muted-foreground',
    chip: 'border-border/70 bg-muted/60 text-muted-foreground',
    focus: 'focus-within:border-border focus-within:ring-border/30',
  },
  error: {
    label: '오류',
    text: 'text-rose-700 dark:text-rose-300',
    soft: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    bar: 'bg-rose-400',
    badge: 'border-border/70 bg-muted/50 text-rose-700 dark:text-rose-300',
    chip: 'border-border/70 bg-muted/45 text-rose-700 dark:text-rose-300',
    focus: 'focus-within:border-rose-400/70 focus-within:ring-rose-400/20',
  },
}

export function collaborationTone(role: CollaborationRole | undefined): CollaborationRoleTone {
  return COLLABORATION_ROLE_TONES[role ?? 'cogo']
}

export function inferWorklogRole(input: {
  actor?: CollaborationRole
  title: string
  stage: string
}): CollaborationRole {
  if (input.actor) return input.actor
  const text = `${input.title} ${input.stage}`.toLowerCase()
  if (text.includes('qwen')) return 'qwen'
  if (text.includes('webmcp') || text.includes('mcp')) return 'webmcp'
  if (text.includes('source') || text.includes('figma') || text.includes('retrieval')) return 'source'
  if (text.includes('runtime') || text.includes('actionflow')) return 'runtime'
  if (text.includes('evidence') || text.includes('validation')) return 'app'
  return 'cogo'
}

export function entryRoleFromTone(tone: 'neutral' | 'success' | 'warning' | undefined): CollaborationRole {
  if (tone === 'success') return 'app'
  if (tone === 'warning') return 'source'
  return 'system'
}
